import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import authApi from "../../../../utils/api/authApi";
import { IMPORT_USERS_CHECK } from "../../../../enums/ApiPaths";

// Same normalisation as the backend check: emails trimmed + lower-cased, phones trimmed
const normalizeEmail = (email) => (email ?? "").trim().toLowerCase();
const normalizePhone = (phone) => (phone ?? "").trim();

// Values that appear on more than one of the given users, e.g. two rows with the same email
const repeated = (values) => {
  const seen = new Set();
  return new Set(values.filter((value) => value && seen.size === seen.add(value).size));
};

/**
 * Asks the backend which emails / phone numbers of the imported users are already used by
 * an account, and flags the ones appearing on more than one of the included rows. The
 * backend rejects the whole import if any included user has either, so they have to be
 * fixed or excluded first.
 * "In use" is checked for every user, included or not, so an excluded row still shows why
 * it can't be imported. Duplicates only count among included users - excluding one of the
 * two rows resolves it - so they are worked out here rather than by the backend, which
 * only sees the list it is sent.
 *
 * Returns conflicts keyed by user id: { emailInUse, phoneInUse, emailDuplicate, phoneDuplicate },
 * and conflictCount - how many *included* users have one (those are what block the import).
 */
export function useImportConflicts(users, includedUsers) {
  const contacts = useMemo(
    () =>
      users
        .filter((user) => user?.email || user?.phoneNumber)
        .map((user) => ({
          email: normalizeEmail(user.email) || null,
          phoneNumber: normalizePhone(user.phoneNumber) || null,
        })),
    [users],
  );

  const check = useQuery({
    queryKey: ["importUsersCheck", contacts],
    queryFn: () =>
      authApi.post(IMPORT_USERS_CHECK, contacts).then((res) => res.data),
    enabled: contacts.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { conflicts, conflictCount } = useMemo(() => {
    const result = {};
    let includedCount = 0;
    if (!check.data) return { conflicts: result, conflictCount: 0 };

    const emailsInUse = new Set(check.data.emailsInUse);
    const phonesInUse = new Set(check.data.phoneNumbersInUse);
    const includedIds = new Set(includedUsers.map((user) => user.id));
    const duplicateEmails = repeated(
      includedUsers.map((user) => normalizeEmail(user.email)),
    );
    const duplicatePhones = repeated(
      includedUsers.map((user) => normalizePhone(user.phoneNumber)),
    );

    users.forEach((user) => {
      const email = normalizeEmail(user.email);
      const phone = normalizePhone(user.phoneNumber);
      const included = includedIds.has(user.id);
      const conflict = {
        emailInUse: !!email && emailsInUse.has(email),
        phoneInUse: !!phone && phonesInUse.has(phone),
        emailDuplicate: included && !!email && duplicateEmails.has(email),
        phoneDuplicate: included && !!phone && duplicatePhones.has(phone),
      };
      if (Object.values(conflict).some(Boolean)) {
        result[user.id] = conflict;
        if (included) includedCount += 1;
      }
    });
    return { conflicts: result, conflictCount: includedCount };
  }, [check.data, users, includedUsers]);

  return {
    conflicts,
    conflictCount,
    isChecking: contacts.length > 0 && check.isFetching,
    isError: check.isError,
  };
}
